import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Container
} from '@mui/material';
import ProductCard from '../components/ProductCard';
import useCart from '../hooks/useCart';
import useViewedProducts from '../hooks/useViewedProducts';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import {
  ArrowBack,
  Share,
  ShoppingBag,
  ShoppingCart,
  CheckCircle,
  Cancel,
  Warning
} from '@mui/icons-material';
import { getProductById, searchProducts } from '../api/apiClient';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { addViewedProduct } = useViewedProducts();
  
  const [productData, setProductData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedColor, setSelectedColor] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('tr');
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loadingSimilarProducts, setLoadingSimilarProducts] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0); // Добавляем состояние для выбранного изображения
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'success' });
  const [showMobileBar, setShowMobileBar] = useState(false);
  
  // Устанавливаем заголовок страницы после определения переменных
  useDocumentTitle(productData?.[selectedCountry]?.[0]?.name || 'Товар');
  
  // Отслеживаем просмотр товара в Яндекс.Метрике
  useEffect(() => {
    if (productData && productData[selectedCountry] && productData[selectedCountry][0]) {
      const product = productData[selectedCountry][0];
      if (window.trackProductView) {
        window.trackProductView(
          product.id,
          product.name,
          product.colors?.[0]?.price || null
        );
      }
    }
  }, [productData, selectedCountry]);
  
  // Обновляем заголовок при изменении данных о товаре или выбранной страны
  useEffect(() => {
    if (productData?.[selectedCountry]?.[0]?.name) {
      const productName = productData[selectedCountry][0].name;
      document.title = `${productName} | ZARA в Россию через @bayer_antalya`;
      
      // Также обновляем Open Graph заголовок
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) {
        ogTitle.setAttribute('content', `${productName} | ZARA в Россию через @bayer_antalya`);
      }
      
      // Обновляем Twitter заголовок
      const twitterTitle = document.querySelector('meta[name="twitter:title"]');
      if (twitterTitle) {
        twitterTitle.setAttribute('content', `${productName} | ZARA в Россию через @bayer_antalya`);
      }
    }
  }, [productData, selectedCountry]);


  const countryMap = {
    us: { name: "США", flag: "🇺🇸", currency: "$" },
    sp: { name: "Испания", flag: "🇪🇸", currency: "€" },
    pl: { name: "Польша", flag: "🇵🇱", currency: "zł" },
    cn: { name: "Китай", flag: "🇨🇳", currency: "¥" },
    vn: { name: "Вьетнам", flag: "🇻🇳", currency: "₫" },
    kz: { name: "Казахстан", flag: "🇰🇿", currency: "₸" },
    by: { name: "Беларусь", flag: "🇧🇾", currency: "Br" }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        
        const response = await getProductById(id);
        
        const discountDebug = [];
        const responseData = response.data;

        Object.entries(responseData || {}).forEach(([countryCode, products]) => {
          if (Array.isArray(products)) {
            products.forEach((item) => {
              if (item?.futurePrice) {
                discountDebug.push({
                  country: countryCode,
                  id: item.id,
                  displayReference: item.displayReference || item.reference,
                  currentPrice: item.price,
                  futurePrice: item.futurePrice?.price,
                  startsAt: item.futurePrice?.description
                });
              } else if (item?.colors?.length) {
                item.colors.forEach((color) => {
                  if (color?.futurePrice) {
                    discountDebug.push({
                      country: countryCode,
                      id: item.id,
                      displayReference: item.displayReference || item.reference,
                      currentPrice: color.price || item.price,
                      futurePrice: color.futurePrice?.price,
                      startsAt: color.futurePrice?.description,
                      level: 'color',
                      colorName: color.name
                    });
                  }
                  if (color?.sizes?.length) {
                    color.sizes.forEach((size) => {
                      if (size?.futurePrice) {
                        discountDebug.push({
                          country: countryCode,
                          id: item.id,
                          displayReference: item.displayReference || item.reference,
                          currentPrice: size.price || color.price || item.price,
                          futurePrice: size.futurePrice?.price,
                          startsAt: size.futurePrice?.description,
                          level: 'size',
                          colorName: color.name,
                          sizeName: size.name
                        });
                      }
                    });
                  }
                });
              }
            });
          }
        });

        console.log('[ProductDetail] Загружен товар', {
          productId: id,
          countries: Object.keys(responseData || {}),
          discountCount: discountDebug.length,
          discountSamples: discountDebug.slice(0, 5)
        });

        setProductData(responseData);
        
        // Добавляем товар в просмотренные
        addViewedProduct(id);
        
        // Выбираем первую доступную страну
        const availableCountries = Object.keys(response.data).filter(
          country => !response.data[country].error
        );
        
        if (availableCountries.length > 0) {
          const firstCountry = availableCountries[0];
          setSelectedCountry(firstCountry);
        }
      } catch (err) {
        console.error('❌ Ошибка при загрузке данных:', err);
        setError(err.response?.data?.error || 'Ошибка при получении данных о товаре');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, addViewedProduct]);

  // Добавляем новый useEffect для загрузки рекомендаций после получения данных о товаре
  useEffect(() => {
    if (productData && Object.keys(productData).length > 0) {
      // Получаем первую доступную страну
      const availableCountries = Object.keys(productData).filter(
        country => !productData[country].error
      );
      
      if (availableCountries.length > 0) {
        const firstCountry = availableCountries[0];
        const product = productData[firstCountry][0];
        
        if (product && product.name) {
          const fetchSimilar = async () => {
            try {
              setLoadingSimilarProducts(true);
              
              // Получаем первые два слова из названия товара
              if (!product.name) {
                setSimilarProducts([]);
                return;
              }
              
              const words = product.name.split(' ').slice(0, 2);
              const searchQuery = words.join(' ');
              
              // Определяем пол товара на основе familyName или sectionName
              let gender = 'WOMAN'; // По умолчанию женская одежда
              
              if (product) {
                if (product.familyName === 'MAN' || product.sectionName === 'MAN') {
                  gender = 'MAN';
                } else if (product.familyName === 'KIDS' || product.sectionName === 'KIDS') {
                  gender = 'KIDS';
                }
              }
              
              // Используем API поиска с простым запросом и указанием пола
              const response = await searchProducts({
                q: searchQuery,
                gender,
                limit: 36
              });
              
              if (response.data && response.data.results) {
                // Фильтруем текущий товар из результатов
                const filteredProducts = response.data.results.filter(product => product.content.id !== id);
                setSimilarProducts(filteredProducts);
              } else {
                setSimilarProducts([]);
              }
            } catch (err) {
              console.error('❌ Ошибка загрузки рекомендаций:', err);
              setSimilarProducts([]);
            } finally {
              setLoadingSimilarProducts(false);
            }
          };
          
          fetchSimilar();
        }
      }
    }
  }, [productData, id]);

  // Отслеживаем выбранные опции для мобильного бара
  useEffect(() => {
    // Проверяем, что все необходимые данные загружены
    if (productData && selectedCountry && selectedSize) {
      // Получаем currentColor безопасно
      const countryData = productData[selectedCountry];
      if (countryData && countryData[0] && countryData[0].colors && countryData[0].colors[selectedColor]) {
        const currentColor = countryData[0].colors[selectedColor];
        const hasAllOptions = selectedSize && currentColor.sizes;
        setShowMobileBar(hasAllOptions);
      } else {
        setShowMobileBar(false);
      }
    } else {
      setShowMobileBar(false);
    }
  }, [productData, selectedCountry, selectedColor, selectedSize]);





  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case 'in_stock':
        return 'success';
      case 'low_on_stock':
        return 'warning';
      case 'out_of_stock':
      case 'coming_soon':
        return 'default';
      default:
        return 'default';
    }
  };

  const getAvailabilityIcon = (availability) => {
    switch (availability) {
      case 'in_stock':
        return <CheckCircle />;
      case 'low_on_stock':
        return <Warning />;
      case 'out_of_stock':
      case 'coming_soon':
        return <Cancel />;
      default:
        return <Cancel />;
    }
  };

  const showSnackbar = (message, type = 'success') => {
    setSnackbar({ open: true, message, type });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const scrollToOptions = () => {
    const optionsSection = document.getElementById('product-options');
    if (optionsSection) {
      optionsSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleAddToCart = () => {
    if (!productData || !selectedSize || !currentColor) {
      return;
    }

    try {
      // Получаем текущую страну
      const currentCountryData = productData[selectedCountry];
      if (!currentCountryData || currentCountryData.error) {
        return;
      }

      // Находим выбранный размер
      const selectedSizeData = currentColor.sizes.find(size => size.name === selectedSize);
      if (!selectedSizeData) {
        return;
      }

      // Проверяем доступность товара
      if (selectedSizeData.availability === 'out_of_stock') {
        showSnackbar('Этот товар временно недоступен', 'error');
        return;
      }

      // Добавляем в корзину
      const success = addToCart(
        id, // spuId
        selectedSizeData.skuId || `${id}-${selectedSize}-${currentColor.name}`, // skuId
        1, // quantity
        selectedSize, // selectedSize
        currentColor.name, // selectedColor
        selectedCountry // country
      );

      if (success) {
        showSnackbar('Товар добавлен в корзину!', 'success');
        
        // Отслеживаем добавление в корзину в Яндекс.Метрике
        if (window.trackAddToCart) {
          window.trackAddToCart(
            id,
            currentCountryData[0].name,
            currentColor.price,
            1
          );
        }
      } else {
        showSnackbar('Ошибка при добавлении товара в корзину', 'error');
      }
    } catch (error) {
      showSnackbar('Ошибка при добавлении товара в корзину', 'error');
    }
  };

  const handleDirectOrder = () => {
    if (!productData || !selectedSize || !currentColor) {
      showSnackbar('Выберите размер и цвет товара', 'error');
      return;
    }

    try {
      // Получаем текущую страну
      const currentCountryData = productData[selectedCountry];
      if (!currentCountryData || currentCountryData.error) {
        showSnackbar('Ошибка получения данных о стране', 'error');
        return;
      }

      // Находим выбранный размер
      const selectedSizeData = currentColor.sizes.find(size => size.name === selectedSize);
      if (!selectedSizeData) {
        showSnackbar('Выбранный размер не найден', 'error');
        return;
      }

      // Проверяем доступность товара
      if (selectedSizeData.availability === 'out_of_stock') {
        showSnackbar('Этот товар временно недоступен', 'error');
        return;
      }

      // Получаем цену товара
      const price = selectedSizeData.price || currentColor.price || 0;

      // Формируем сообщение для заказа
      const message = `Здравствуйте! Хочу оформить заказ на товар:\n\n` +
        `📦 ${currentProduct.name}\n` +
        `🔢 Артикул: ${currentProduct.displayReference}\n` +
        `📏 Размер: ${selectedSize}\n` +
        `🎨 Цвет: ${currentColor.name}\n` +
        `💰 Цена: ${price} ₽\n` +
        `🌍 Страна: ${getCountryName(selectedCountry)}\n` +
        `🔗 Ссылка на товар: ${window.location.href}`;

      // Открываем Telegram для оформления заказа
      const telegramUrl = `https://t.me/olesyalalal?text=${encodeURIComponent(message)}`;
      window.open(telegramUrl, '_blank');
      
      showSnackbar('Открываем Telegram для оформления заказа!', 'success');
    } catch (error) {
      showSnackbar('Ошибка при оформлении заказа', 'error');
    }
  };

  const getCountryName = (countryCode) => {
    const countryMap = {
      us: "США",
      sp: "Испания",
      tr: "Турция",
      ee: "Эстония",
      cn: "Китай",
      vn: "Вьетнам",
      kz: "Казахстан",
      by: "Беларусь"
    };
    return countryMap[countryCode] || countryCode.toUpperCase();
  };



  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Вернуться на главную
        </Button>
      </Container>
    );
  }

  // Дополнительные проверки безопасности
  if (!productData || !productData[selectedCountry] || productData[selectedCountry].error) {
    return (
      <Container maxWidth="md">
        <Alert severity="warning" sx={{ mt: 4 }}>
          Товар не найден в выбранной стране
        </Alert>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Вернуться на главную
        </Button>
      </Container>
    );
  }

  const currentProduct = productData[selectedCountry][0];

  
  // Проверяем, что у товара есть цвета
  if (!currentProduct || !currentProduct.colors || currentProduct.colors.length === 0) {
    return (
      <Container maxWidth="md">
        <Alert severity="warning" sx={{ mt: 4 }}>
          Информация о товаре неполная или отсутствует
        </Alert>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Вернуться на главную
        </Button>
      </Container>
    );
  }

  const currentColor = currentProduct.colors[selectedColor];
  const selectedSizeData = selectedSize
    ? currentColor.sizes?.find(size => size.name === selectedSize)
    : null;

  const formatPrice = (value) => {
    if (value === undefined || value === null || value === 'Нет данных') return 'Цена не указана';
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(value);
  };

  const currentPrice =
    selectedSizeData?.price ??
    currentColor?.price ??
    currentProduct?.price ??
    selectedSizeData?.originalPrice ??
    currentColor?.originalPrice ??
    currentProduct?.originalPrice ??
    null;
  const futurePriceSource =
    selectedSizeData?.futurePrice ||
    currentColor.futurePrice ||
    currentProduct.futurePrice;

  const hasDiscount = Boolean(futurePriceSource?.price);
  const discountPrice = futurePriceSource?.price ?? null;
  const discountLabel = futurePriceSource?.discountLabel ||
    futurePriceSource?.displayDiscountPercentage ||
    futurePriceSource?.discountPercentage;
  const discountStartsAt = futurePriceSource?.description || null;

  // Проверяем, что выбранный цвет существует
  if (!currentColor) {
    return (
      <Container maxWidth="md">
        <Alert severity="warning" sx={{ mt: 4 }}>
          Выбранный цвет недоступен
        </Alert>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Вернуться на главную
        </Button>
      </Container>
    );
  }



  return (
    <Container maxWidth={false} sx={{ px: { xs: 1, md: 3 }, pb: { xs: 10, md: 0 } }}>
      {/* Кнопка назад */}
      <Box sx={{ mb: { xs: 2, md: 4 } }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => window.history.back()}
          sx={{ textTransform: 'none' }}
        >
          Назад
        </Button>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        gap: { xs: 2, md: 3 }, 
        flexDirection: { xs: 'column', md: 'row' },
        minHeight: { xs: 'auto', md: '70vh' }
      }}>
        {/* Левая колонка - Галерея изображений */}
        <Box 
          className="product-detail-gallery"
          sx={{ 
            flex: { xs: '1 1 100%', md: '0 0 50%' },
            minWidth: { xs: '100%', md: '50%' }
          }}
        >
          <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', height: 'fit-content' }}>
            {currentColor.images && currentColor.images.length > 0 ? (
              <Box sx={{ position: 'relative' }}>
                {/* Основное изображение */}
                <Box sx={{ 
                  width: '100%', 
                  height: { xs: '50vh', md: '70vh' },
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <img
                    src={currentColor.images[selectedImageIndex]}
                    alt={`${currentColor.name} ${selectedImageIndex + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      objectPosition: 'center'
                    }}
                  />
                </Box>
                
                {/* Навигационные стрелки (если больше 1 изображения) */}
                {currentColor.images.length > 1 && (
                  <>
                    {/* Стрелка влево */}
                    <IconButton
                      onClick={() => setSelectedImageIndex(prev => 
                        prev === 0 ? currentColor.images.length - 1 : prev - 1
                      )}
                      sx={{
                        position: 'absolute',
                        left: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        color: 'black',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)'
                        }
                      }}
                    >
                      <ArrowBack />
                    </IconButton>
                    
                    {/* Стрелка вправо */}
                    <IconButton
                      onClick={() => setSelectedImageIndex(prev => 
                        prev === currentColor.images.length - 1 ? 0 : prev + 1
                      )}
                      sx={{
                        position: 'absolute',
                        right: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        color: 'black',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)'
                        }
                      }}
                    >
                      <ArrowBack sx={{ transform: 'rotate(180deg)' }} />
                    </IconButton>
                  </>
                )}
                
                {/* Индикатор текущего изображения */}
                {currentColor.images.length > 1 && (
                  <Box sx={{
                    position: 'absolute',
                    bottom: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: 1
                  }}>
                    {currentColor.images.map((_, index) => (
                      <Box
                        key={`indicator-${index}`}
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: index === selectedImageIndex ? 'white' : 'rgba(255, 255, 255, 0.5)',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => setSelectedImageIndex(index)}
                      />
                    ))}
                  </Box>
                )}
                
                {/* Миниатюры под основным изображением */}
                {currentColor.images.length > 1 && (
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    p: { xs: 1, md: 2 }, 
                    backgroundColor: 'transparent',
                    borderTop: '1px solid #eee',
                    justifyContent: 'center'
                  }}>
                    {currentColor.images.map((image, index) => (
                      <Box
                        key={`thumbnail-${index}`}
                        onClick={() => setSelectedImageIndex(index)}
                        sx={{
                          position: 'relative',
                          cursor: 'pointer',
                          border: selectedImageIndex === index ? '2px solid #000' : 'none',
                          borderRadius: 2,
                          overflow: 'hidden',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            borderColor: selectedImageIndex === index ? '#000' : 'transparent'
                          }
                        }}
                      >
                        <img
                          src={image}
                          alt={`${currentColor.name} ${index + 1}`}
                          style={{
                            width: 60,
                            height: 60,
                            objectFit: 'cover'
                          }}
                        />
                        
                        {/* Индикатор выбранного изображения */}
                        {selectedImageIndex === index && (
                          <Box sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            backgroundColor: '#000',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <CheckCircle sx={{ color: 'white', fontSize: 12 }} />
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ 
                height: 400, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: 'transparent'
              }}>
                <Typography color="text.secondary">
                  Изображения недоступны
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>

        {/* Правая колонка - Информация о товаре */}
        <Box sx={{ 
          flex: { xs: '1 1 100%', md: '0 0 50%' },
          minWidth: { xs: '100%', md: '50%' }
        }}>
          <Box sx={{ 
            position: { xs: 'static', md: 'sticky' }, 
            top: { md: 20 },
            height: { xs: 'auto', md: '70vh' },
            overflowY: { xs: 'visible', md: 'auto' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: { xs: 'flex-start', md: 'space-between' }
          }}>
            {/* Название товара */}
            <Typography variant="h5" component="h1" sx={{ mb: { xs: 1, md: 2 }, fontWeight: 300, lineHeight: 1.2, fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
              {currentProduct.name}
            </Typography>
            
            {/* Артикул */}
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: { xs: 1, md: 2 },
                fontSize: { xs: '0.8rem', md: '0.9rem' },
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                  color: 'primary.main'
                }
              }}
              onClick={() => {
                navigator.clipboard.writeText(currentProduct.displayReference);
                showSnackbar('Артикул скопирован в буфер обмена!', 'success');
              }}
            >
              Артикул: {currentProduct.displayReference}
            </Typography>
            
            {/* Цена */}
            {hasDiscount ? (
              <Box sx={{ mb: { xs: 2, md: 3 } }}>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 300, fontSize: { xs: '1.5rem', md: '1.8rem' }, color: 'error.main', lineHeight: 1.1 }}
                >
                  {formatPrice(discountPrice)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                  <Typography
                    variant="body1"
                    sx={{ textDecoration: 'line-through', color: 'text.secondary', fontSize: { xs: '0.95rem', md: '1rem' } }}
                  >
                    {formatPrice(currentPrice)}
                  </Typography>
                  {discountLabel && (
                    <Chip
                      label={discountLabel}
                      color="error"
                      size="small"
                      sx={{ height: 22, fontWeight: 600, fontSize: '0.8rem' }}
                    />
                  )}
                </Box>
                {discountStartsAt && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {discountStartsAt}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="h4" sx={{ mb: { xs: 2, md: 3 }, fontWeight: 300, fontSize: { xs: '1.5rem', md: '1.8rem' } }}>
                {formatPrice(currentPrice)}
              </Typography>
            )}

            {/* Выбор опций товара */}
            <Box id="product-options" sx={{ mb: { xs: 2, md: 3 } }}>
              {/* Выбор страны */}
              <Box sx={{ mb: { xs: 2, md: 3 } }}>
              <Typography variant="h6" sx={{ mb: { xs: 1, md: 2 }, fontWeight: 500 }}>
                Страна
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {Object.entries(countryMap).map(([code, country]) => {
                  const isAvailable = productData[code] && !productData[code].error;
                  return (
                    <Chip
                      key={code}
                      label={`${country.flag} ${country.name}`}
                      onClick={() => setSelectedCountry(code)}
                      variant={selectedCountry === code ? 'filled' : 'outlined'}
                      color={selectedCountry === code ? 'primary' : 'default'}
                      disabled={!isAvailable}
                      sx={{ 
                        cursor: isAvailable ? 'pointer' : 'default',
                        opacity: isAvailable ? 1 : 0.5
                      }}
                    />
                  );
                })}
              </Box>
            </Box>

            {/* Выбор цвета */}
            {currentProduct.colors.length > 1 && (
              <Box sx={{ mb: { xs: 2, md: 3 } }}>
                <Typography variant="h6" sx={{ mb: { xs: 1, md: 2 }, fontWeight: 500 }}>
                  Цвет
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {currentProduct.colors.map((color, index) => (
                    <Box
                      key={`color-${color.name}-${index}`}
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: color.hexCode,
                        border: selectedColor === index ? '3px solid #000' : '2px solid #ddd',
                        cursor: 'pointer',
                        position: 'relative'
                      }}
                      onClick={() => {
                        setSelectedColor(index);
                        setSelectedImageIndex(0); // Сбрасываем выбранное изображение
                        setSelectedSize(null); // Сбрасываем выбранный размер при смене цвета
                      }}
                    >
                      {selectedColor === index && (
                        <CheckCircle 
                          sx={{ 
                            position: 'absolute', 
                            top: -5, 
                            right: -5, 
                            color: 'white',
                            backgroundColor: '#000',
                            borderRadius: '50%',
                            fontSize: 20
                          }} 
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Выбор размера */}
            {currentColor.sizes && currentColor.sizes.length > 0 && (
              <Box sx={{ mb: { xs: 2, md: 3 } }}>
                <Typography variant="h6" sx={{ mb: { xs: 1, md: 2 }, fontWeight: 500 }}>
                  Размер
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {currentColor.sizes.map((size, index) => {
                    const isUnavailable = size.availability === 'out_of_stock' || size.availability === 'coming_soon';
                    return (
                      <Chip
                        key={`size-${size.name}-${index}`}
                        label={size.name}
                        onClick={isUnavailable ? undefined : () => setSelectedSize(size.name)}
                        variant={selectedSize === size.name ? 'filled' : 'outlined'}
                        color={getAvailabilityColor(size.availability)}
                        icon={getAvailabilityIcon(size.availability)}
                        disabled={isUnavailable}
                        sx={{ 
                          cursor: isUnavailable ? 'not-allowed' : 'pointer',
                          backgroundColor: selectedSize === size.name ? 'black' : 'transparent',
                          color: selectedSize === size.name ? 'white' : 'black',
                          borderColor: 'black',
                          opacity: isUnavailable ? 0.5 : 1,
                          // Кастомные цвета для разных статусов доступности
                          ...(size.availability === 'low_on_stock' && {
                            borderColor: 'orange',
                            color: 'orange',
                            '& .MuiChip-icon': {
                              color: 'orange !important'
                            }
                          }),
                          ...(size.availability === 'in_stock' && {
                            borderColor: 'green',
                            color: 'green',
                            '& .MuiChip-icon': {
                              color: 'green !important'
                            }
                          }),
                          ...(size.availability === 'out_of_stock' && {
                            borderColor: 'gray',
                            color: 'gray',
                            '& .MuiChip-icon': {
                              color: 'gray !important'
                            }
                          }),
                          ...(size.availability === 'coming_soon' && {
                            borderColor: 'gray',
                            color: 'gray',
                            '& .MuiChip-icon': {
                              color: 'gray !important'
                            }
                          }),
                          '&:hover': {
                            backgroundColor: isUnavailable ? 'transparent' : (selectedSize === size.name ? 'black' : 'rgba(0, 0, 0, 0.1)')
                          }
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}





            {/* Кнопки действий - только на десктопе */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, mb: 3 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={selectedSize ? <ShoppingBag /> : null}
                fullWidth
                onClick={selectedSize ? handleAddToCart : undefined}
                disabled={!selectedSize}
                sx={{ 
                  py: 1.5, 
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  backgroundColor: selectedSize ? 'primary.main' : '#666',
                  '&:hover': {
                    backgroundColor: selectedSize ? 'primary.dark' : '#555'
                  }
                }}
              >
                {selectedSize ? 'Добавить в корзину' : 'Выберите опции товара'}
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<ShoppingCart />}
                onClick={selectedSize ? handleDirectOrder : undefined}
                disabled={!selectedSize}
                sx={{ 
                  border: '1px solid #000',
                  color: '#000',
                  textTransform: 'none',
                  minWidth: 'auto',
                  px: 2,
                  '&:hover': {
                    backgroundColor: '#000',
                    color: '#fff',
                    borderColor: '#000'
                  }
                }}
              >
                Оформить заказ
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<Share />}
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: currentProduct.name,
                      text: `Посмотрите на этот товар: ${currentProduct.name}`,
                      url: window.location.href
                    });
                  } else {
                    // Fallback для браузеров без поддержки Web Share API
                    navigator.clipboard.writeText(window.location.href);
                    showSnackbar('Ссылка скопирована в буфер обмена!', 'success');
                  }
                }}
                sx={{ 
                  border: '1px solid #ddd',
                  textTransform: 'none',
                  minWidth: 'auto',
                  px: 2
                }}
              >
                Поделиться
              </Button>
            </Box>
          </Box>
        </Box>
        </Box>
      </Box>



      {/* Рекомендации */}
      <Box sx={{ mt: { xs: 4, md: 6 } }}>
        <Typography variant="h5" sx={{ mb: { xs: 2, md: 3 }, fontWeight: 400 }}>
          Похожие товары
        </Typography>
        
        {loadingSimilarProducts ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={40} />
          </Box>
        ) : similarProducts.length > 0 ? (
          <Box className="product-grid">
            {similarProducts.map((product, index) => {
              // Извлекаем данные из структуры API поиска
              const productData = product.content;
              
              // Создаем объект с правильной структурой для ProductCard
                const cardProduct = {
                  id: productData.id,
                  name: productData.name,
                  price: productData.price,
                  futurePrice: productData.futurePrice,
                  priceInRub: productData.price,
                  reference: productData.reference,
                  displayReference: productData.displayReference,
                // Извлекаем изображение так же, как в SearchResultsPage
                mainImage: (() => {
                  const firstColor = productData.detail?.colors?.[0];
                  if (firstColor?.xmedia && firstColor.xmedia.length > 0) {
                    return firstColor.xmedia[0].url;
                  }
                  // Fallback на другие поля
                  return productData.mainImage || productData.detail?.mainImage || productData.detail?.images?.[0]?.url;
                })(),
                description: productData.description,
                colors: productData.detail?.colors || []
              };
              
              return (
                <ProductCard
                  key={`${cardProduct.id}-${index}`}
                  product={cardProduct}
                  onClick={() => navigate(`/product/${cardProduct.id}`)}
                  showColors={true}
                  showNewBadge={false}
                  variant="default"
                />
              );
            })}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Рекомендации временно недоступны
            </Typography>
          </Box>
        )}
      </Box>

      {/* Мобильный бар внизу экрана */}
      {currentColor && (
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'white',
            borderTop: '1px solid #e0e0e0',
            boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
            zIndex: 1000,
            p: 2
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Цена и кнопки в одну строку */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Цена */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  Цена
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {currentColor.price !== 'Нет данных' ? `${currentColor.price} ₽` : 'Цена не указана'}
                </Typography>
              </Box>
              
              {/* Кнопка "Поделиться" - иконка рядом с основной кнопкой */}
              <IconButton
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: currentProduct.name,
                      text: `Посмотрите на этот товар: ${currentProduct.name}`,
                      url: window.location.href
                    });
                  } else {
                    // Fallback для браузеров без поддержки Web Share API
                    navigator.clipboard.writeText(window.location.href);
                    showSnackbar('Ссылка скопирована в буфер обмена!', 'success');
                  }
                }}
                sx={{ 
                  border: '1px solid #ddd',
                  color: '#666',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.05)',
                    borderColor: '#999',
                    color: '#333'
                  }
                }}
              >
                <Share />
              </IconButton>
              
              {/* Кнопка действия */}
              {showMobileBar ? (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<ShoppingBag />}
                  onClick={handleAddToCart}
                  sx={{ 
                    textTransform: 'none',
                    px: 3,
                    py: 1.5,
                    backgroundColor: 'black',
                    '&:hover': {
                      backgroundColor: '#333'
                    }
                  }}
                >
                  В корзину
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={scrollToOptions}
                  sx={{ 
                    textTransform: 'none',
                    px: 3,
                    py: 1.5,
                    borderColor: 'black',
                    color: 'black',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.05)',
                      borderColor: 'black'
                    }
                  }}
                >
                  Выбрать опции
                </Button>
              )}
            </Box>
            
            {/* Кнопка "Оформить заказ" под основной кнопкой */}
            {showMobileBar && (
              <Button
                variant="outlined"
                size="large"
                startIcon={<ShoppingCart />}
                onClick={handleDirectOrder}
                fullWidth
                sx={{ 
                  textTransform: 'none',
                  py: 1.5,
                  borderColor: '#000',
                  color: '#000',
                  '&:hover': {
                    backgroundColor: '#000',
                    color: '#fff',
                    borderColor: '#000'
                  }
                }}
              >
                Оформить заказ
              </Button>
            )}
            

          </Box>
        </Box>
      )}

      {/* Snackbar для уведомлений */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbar-root': {
            bottom: { xs: 16, md: 24 }
          }
        }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.type}
          variant="filled"
          sx={{ 
            width: '100%',
            fontSize: { xs: '0.9rem', md: '1rem' },
            '& .MuiAlert-message': {
              fontWeight: 500
            },
            // Кастомные цвета в стиле Zara (черно-белая гамма)
            ...(snackbar.type === 'success' && {
              backgroundColor: '#000000',
              color: '#ffffff',
              '& .MuiAlert-icon': {
                color: '#ffffff'
              },
              '& .MuiAlert-action .MuiIconButton-root': {
                color: '#ffffff',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }
            }),
            ...(snackbar.type === 'error' && {
              backgroundColor: '#ffffff',
              color: '#000000',
              border: '1px solid #000000',
              '& .MuiAlert-icon': {
                color: '#000000'
              },
              '& .MuiAlert-action .MuiIconButton-root': {
                color: '#000000',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.1)'
                }
              }
            })
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProductDetailPage;
